import { config } from '../config.ts';
import { createStorageTexture2D, createUniformBuffer, writeUniform } from '../gpu/buffers.ts';
import { formats } from '../gpu/formats.ts';
import { createComputePipeline } from '../gpu/pipelines.ts';
import type { HeightmapModule } from './heightmap.ts';
import hashWgsl from './hash.wgsl?raw';
import biomeWgsl from './biome.wgsl?raw';

const wgsl = hashWgsl + '\n' + biomeWgsl;

export interface BiomeModule {
  readonly texture: GPUTexture;
  readonly view: GPUTextureView;
  destroy(): void;
}

export async function createBiome(
  device: GPUDevice,
  height: HeightmapModule,
  opts: { seed: number },
): Promise<BiomeModule> {
  const size = config.heightmapSize;

  const texture = createStorageTexture2D(
    device,
    [size, size],
    formats.biomeStorage,
    'biome',
  );
  const view = texture.createView({ label: 'biome.view' });

  const paramsBuffer = createUniformBuffer(device, 16, 'biome.params');
  writeUniform(device, paramsBuffer, new Uint32Array([opts.seed >>> 0, 0, 0, 0]));

  const pipeline = createComputePipeline(device, wgsl, 'main', 'auto', 'biome');

  const bindGroup = device.createBindGroup({
    label: 'biome.bindGroup',
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: height.view },
      { binding: 1, resource: view },
      { binding: 2, resource: { buffer: paramsBuffer } },
    ],
  });

  const encoder = device.createCommandEncoder({ label: 'biome.encoder' });
  const pass = encoder.beginComputePass({ label: 'biome.pass' });
  pass.setPipeline(pipeline);
  pass.setBindGroup(0, bindGroup);
  const groups = Math.ceil(size / 8);
  pass.dispatchWorkgroups(groups, groups, 1);
  pass.end();
  device.queue.submit([encoder.finish()]);

  return {
    texture,
    view,
    destroy() {
      texture.destroy();
      paramsBuffer.destroy();
    },
  };
}
